import type { FundSummary } from '../lib/api'

export interface PortfolioRow {
  ticker: string
  allocation: string // kept as string so the input can be empty while typing
}

interface Props {
  rows: PortfolioRow[]
  funds: FundSummary[]
  onChange: (rows: PortfolioRow[]) => void
}

export default function PortfolioInput({ rows, funds, onChange }: Props) {
  function update(index: number, patch: Partial<PortfolioRow>) {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }
  function addRow() {
    onChange([...rows, { ticker: '', allocation: '' }])
  }
  function removeRow(index: number) {
    onChange(rows.length > 1 ? rows.filter((_, i) => i !== index) : rows)
  }

  const sum = rows.reduce((acc, r) => acc + (parseFloat(r.allocation) || 0), 0)

  return (
    <div className="card">
      <h2>Your portfolio</h2>
      <p className="muted">
        Enter each fund ticker and its share of your portfolio. Allocations should add up to 100%.
      </p>

      <datalist id="fund-tickers">
        {funds.map((f) => (
          <option key={f.isin} value={f.ticker}>
            {f.name}
          </option>
        ))}
      </datalist>

      <div className="portfolio-grid" role="table" aria-label="Portfolio holdings">
        <div className="portfolio-head" role="row">
          <span role="columnheader">Fund ticker</span>
          <span role="columnheader">Allocation %</span>
          <span />
        </div>
        {rows.map((row, i) => (
          <div className="portfolio-row" role="row" key={i}>
            <input
              list="fund-tickers"
              placeholder="e.g. VTI"
              value={row.ticker}
              spellCheck={false}
              autoCapitalize="characters"
              onChange={(e) => update(i, { ticker: e.target.value.toUpperCase() })}
              aria-label={`Ticker for row ${i + 1}`}
            />
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max="100"
              step="0.1"
              placeholder="0"
              value={row.allocation}
              onChange={(e) => update(i, { allocation: e.target.value })}
              aria-label={`Allocation for row ${i + 1}`}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => removeRow(i)}
              aria-label={`Remove row ${i + 1}`}
              disabled={rows.length === 1}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <div className="portfolio-footer">
        <button type="button" className="link-btn" onClick={addRow}>
          + Add fund
        </button>
        <span className={`alloc-sum ${Math.abs(sum - 100) > 0.01 ? 'warn' : 'ok'}`}>
          Total: {sum.toFixed(1)}%
        </span>
      </div>
    </div>
  )
}
